import { m } from 'motion/react';
import { containerVariants, viewPort } from 'utils/framer';
import KIAImage from './ui/image/KIAImage';
import KiaImagePlaceholder from './ui/KiaImagePlaceholder';
import LinkButton from './ui/LinkButton';

export type ContentBlock = {
  title?: string;
  text?: string;
  list?: string[];
  image: {
    src: string;
    alt: string;
    sizes?: string;
    className?: string;
  };
  link?: {
    url: string;
    text: string;
  };
};

interface ContentBlockProps {
  blocks: ContentBlock[];
  firstBlockReversed?: boolean;
}

const ContentBlock = ({ blocks, firstBlockReversed }: ContentBlockProps) => {
  if (!blocks || blocks.length < 1) return null;
  return (
    <m.ul
      initial="hidden"
      whileInView="visible"
      viewport={viewPort}
      variants={containerVariants}
      className="flex flex-col gap-14 md:gap-10 lg:gap-24"
    >
      {blocks.map((block, index) => {
        const isOdd = firstBlockReversed
          ? (index + 1) % 2 === 0
          : (index + 1) % 2 != 0;
        return (
          <m.li
            initial="hidden"
            whileInView="visible"
            viewport={viewPort}
            variants={containerVariants}
            key={index}
            className={`grid grid-cols-1 lg:grid-cols-2 lg:items-center gap-4 lg:gap-8`}
          >
            <div
              className={`w-full ${isOdd ? 'order-1' : 'order-1 lg:order-2'}`}
            >
              {block.image.src === 'placeholder' ? (
                <KiaImagePlaceholder className="aspect-[3/2]" />
              ) : (
                <KIAImage
                  {...block.image}
                  className={`aspect-[3/2] object-cover ${block.image.className || ''}`}
                />
              )}
            </div>
            <div
              className={`flex flex-col gap-3 lg:gap-4 w-full px-4 lg:px-10 ${isOdd ? 'order-2' : 'order-2 lg:order-1'}`}
            >
              {block.title ? (
                <h2 className="font-semibold antialiased"> {block.title}</h2>
              ) : null}
              {block.text ? (
                <p
                  className="font-light"
                  dangerouslySetInnerHTML={{ __html: block.text }}
                />
              ) : null}
              {block.list ? (
                <ul className="ml-4 grid gap-2">
                  {block.list.map((item, index) => (
                    <li className="list-disc" key={index}>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : null}
              {block.link.url ? (
                <LinkButton
                  className="mt-2"
                  url={block.link.url}
                  text={block.link.text}
                />
              ) : null}
            </div>
          </m.li>
        );
      })}
    </m.ul>
  );
};

export default ContentBlock;
